import type { GameState, InventoryItem, TrashKind, UsableItemId } from '../contract.js';
import { TRASH_STACK_LIMIT, type ResourceId } from '../domain/content.js';
import { addResourceToSlotArray, resourceCapacity } from './resource-stacks.js';

export function selectedItem(state: GameState): InventoryItem | null {
  return state.inventory.slots[state.player.selectedSlot] ?? null;
}

export function firstEmptySlot(state: GameState): number {
  return state.inventory.slots.findIndex((slot) => slot === null);
}

export function hasEmptySlot(state: GameState): boolean {
  return firstEmptySlot(state) >= 0;
}

export function trashCapacity(slots: Array<InventoryItem | null>): number {
  let capacity = 0;
  for (const slot of slots) {
    if (slot === null) capacity += TRASH_STACK_LIMIT;
    else if (slot.kind === 'trash') capacity += Math.max(0, TRASH_STACK_LIMIT - slot.quantity);
  }
  return capacity;
}

export function hasTrashCapacity(state: GameState, amount = 1): boolean {
  return trashCapacity(state.inventory.slots) >= amount;
}

export function addTrashItem(state: GameState, resource: TrashKind, amount = 1): boolean {
  return addTrashToSlotArray(state, state.inventory.slots, resource, amount);
}

export function addTrashToSlotArray(
  state: GameState,
  slots: Array<InventoryItem | null>,
  resource: TrashKind,
  amount: number,
): boolean {
  if (amount <= 0) return true;
  if (trashCapacity(slots) < amount) return false;
  let remaining = amount;
  for (const slot of slots) {
    if (slot?.kind !== 'trash' || slot.quantity >= TRASH_STACK_LIMIT) continue;
    const added = Math.min(TRASH_STACK_LIMIT - slot.quantity, remaining);
    slot.quantity += added;
    remaining -= added;
    if (remaining <= 0) return true;
  }
  while (remaining > 0) {
    const slot = slots.findIndex((candidate) => candidate === null);
    if (slot < 0) return false;
    const quantity = Math.min(TRASH_STACK_LIMIT, remaining);
    slots[slot] = {
      uid: state.inventory.nextItemUid++,
      kind: 'trash',
      itemId: 'trash',
      resource,
      quantity,
    };
    remaining -= quantity;
  }
  return true;
}

export function addResourceItem(state: GameState, resource: ResourceId, amount: number): boolean {
  if (amount <= 0) return true;
  if (resourceCapacity(state.inventory.slots, resource) < amount) return false;
  addResourceToSlotArray(state, state.inventory.slots, resource, amount);
  state.resources[resource] += amount;
  return true;
}

export function addUsableItem(state: GameState, itemId: UsableItemId, amount = 1): boolean {
  if (amount <= 0) return true;
  for (let i = 0; i < amount; i++) {
    const slot = firstEmptySlot(state);
    if (slot < 0) return false;
    state.inventory.slots[slot] = {
      uid: state.inventory.nextItemUid++,
      kind: 'item',
      itemId,
      quantity: 1,
    };
  }
  return true;
}

export function hasUsableItem(state: GameState, itemId: UsableItemId): boolean {
  return state.inventory.slots.some((slot) => slot?.kind === 'item' && slot.itemId === itemId);
}

export function removeUsableItem(state: GameState, itemId: UsableItemId): boolean {
  const index = state.inventory.slots.findIndex((slot) => slot?.kind === 'item' && slot.itemId === itemId);
  if (index < 0) return false;
  state.inventory.slots[index] = null;
  return true;
}

export function removeResourceItem(state: GameState, resource: ResourceId, amount: number): boolean {
  if (state.resources[resource] < amount) return false;
  let remaining = amount;
  for (let i = 0; i < state.inventory.slots.length && remaining > 0; i++) {
    const slot = state.inventory.slots[i];
    if (slot?.kind !== 'resource' || slot.resource !== resource) continue;
    const removed = Math.min(slot.quantity, remaining);
    slot.quantity -= removed;
    remaining -= removed;
    if (slot.quantity <= 0) state.inventory.slots[i] = null;
  }
  if (remaining > 0) return false;
  state.resources[resource] -= amount;
  return true;
}

export function removeSelectedItem(state: GameState): InventoryItem | null {
  const item = selectedItem(state);
  if (item === null) return null;
  state.inventory.slots[state.player.selectedSlot] = null;
  return item;
}

export function compactSelectedSlot(state: GameState): void {
  if (state.player.selectedSlot >= state.inventory.slots.length) state.player.selectedSlot = state.inventory.slots.length - 1;
  if (state.player.selectedSlot < 0) state.player.selectedSlot = 0;
}
